import React, { useState, MouseEvent } from 'react'
import Menu from '@mui/material/Menu'
import FilterListIcon from '@mui/icons-material/FilterList'
import FilterPopover from '../../Common/Filter/FilterPopover'
import {
  Badge,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material'
import { fontSizes } from '../../Common/Typography/TypographyStyles'

// Define the props type
interface EventFilterProps {
  onFilterChange: (filters: Record<string, string[]>) => void
}

// Define the filter structure
interface FilterItem {
  id: string
  name: string
  title?: string
  filterSubItem: { id: string; label: string; value: string }[]
  type?: 'checkbox' | 'radio'
}

const EventFilter: React.FC<EventFilterProps> = ({ onFilterChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const defaultFilters = {
    Category: [] as string[],
    Days: ['0'] as string[], // Default to 'All'
  }

  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>(defaultFilters)
  const [countOfSelectedFilter, setCountOfSelectedFilter] = useState(0)

  // Filter items for different pages
  const filterItems: FilterItem[] = [
    {
      id: 'category-all-check-box',
      name: 'Category',
      filterSubItem: [
        { id: 'tids-check-box', label: 'TIDS', value: 'TIDS' },
        { id: 'happy-here-check-box', label: '#HAPPYHERE', value: 'happyhere' },
        { id: 'team-event-check-box', label: 'TEAM EVENT', value: 'teamEvent' },
        { id: 'cop-check-box', label: 'COP', value: 'COP' },
      ],
      type: 'checkbox',
    },
    // {
    //   id: 'days-radio-group',
    //   title: 'Date Range',
    //   name: 'Days',
    //   filterSubItem: [
    //     { id: 'all-radio', label: 'All', value: '0' },
    //     { id: '7-days-radio', label: '7 Days', value: '7' },
    //     { id: '30-days-radio', label: '30 Days', value: '30' },
    //     { id: '90-days-radio', label: '90 Days', value: '90' },
    //   ],
    //   type: 'radio',
    // },
  ]

  // Handle opening of the filter menu
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  // Handle closing of the filter menu
  const handleClose = () => {
    setAnchorEl(null)
  }

  // Handle filter change
  const handleFilterChange = (type: string, value: string, checked: boolean) => {
    let updatedFilters: string[]

    if (type === 'Days') {
      // For radio buttons, we always select the new value
      updatedFilters = [value]
    } else {
      // For checkboxes, we add or remove the value
      updatedFilters = checked
        ? [...selectedFilters[type], value]
        : selectedFilters[type].filter((item) => item !== value)
    }

    const newFilters = {
      ...selectedFilters,
      [type]: updatedFilters,
    }

    setSelectedFilters(newFilters)
    onFilterChange(newFilters)

    const countOfSelectedFilters = Object.entries(newFilters)
      .filter(([key, value]) => key !== 'Days' || value[0] !== '0')
      .flatMap(([_, value]) => value).length
    setCountOfSelectedFilter(countOfSelectedFilters)
  }

  // Handle select all change (only for checkbox types)
  const handleSelectAllChange = (type: string, checked: boolean) => {
    const filterItem = filterItems.find((item) => item.name === type)
    if (filterItem?.type !== 'checkbox') return

    const allValues = filterItem.filterSubItem.map((item) => item.value)

    const newFilters = {
      ...selectedFilters,
      [type]: checked ? allValues : [],
    }

    setSelectedFilters(newFilters)
    onFilterChange(newFilters)

    const countOfSelectedFilters = Object.entries(newFilters)
      .filter(([key, value]) => key !== 'Days' || value[0] !== '0')
      .flatMap(([_, value]) => value).length
    setCountOfSelectedFilter(countOfSelectedFilters)
  }

  // Handle reset of filters
  const handleResetFilters = () => {
    setSelectedFilters(defaultFilters)
    onFilterChange(defaultFilters)
    setCountOfSelectedFilter(0)
  }

  return (
    <>
      <Badge badgeContent={countOfSelectedFilter} color='error'>
        <Button
          key='event-filter-button'
          id='event-filter-button'
          color='success'
          startIcon={<FilterListIcon />}
          onClick={handleClick}
          size='small'
          aria-haspopup='true'
          aria-label='event-filter-button'
          aria-controls={open ? 'event-filter-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
        >
          Filter
        </Button>
      </Badge>
      <Menu
        key='event-filter-menu'
        id='event-filter-menu'
        MenuListProps={{
          'aria-labelledby': 'event-filter-button',
          sx: {
            padding: 0,
          },
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: '250px',
          },
        }}
      >
        <FilterPopover
          key='filter-popover'
          filterItems={filterItems.filter((item) => item.type === 'checkbox')}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onSelectAllChange={handleSelectAllChange}
        />
        {filterItems.map((filterItem) => (
          <>
            {filterItem.type === 'radio' && (
              <FormControl key={filterItem.id} component='fieldset' sx={{ padding: '8px 16px' }}>
                <FormLabel
                  component='legend'
                  sx={{
                    fontWeight: 700,
                    fontSize: fontSizes.size14,
                  }}
                >
                  {filterItem.title?.toUpperCase()}
                </FormLabel>

                <RadioGroup
                  aria-label={filterItem.name}
                  name={filterItem.name}
                  value={selectedFilters[filterItem.name][0]}
                  onChange={(e) => handleFilterChange(filterItem.name, e.target.value, true)}
                  sx={{
                    '& .MuiRadio-root': {
                      padding: '2px',
                    },
                    marginLeft: 2,
                  }}
                >
                  {filterItem.filterSubItem.map((item) => (
                    <FormControlLabel
                      sx={{
                        marginLeft: 2,
                        '& .MuiFormControlLabel-label': {
                          fontSize: fontSizes.size14,
                        },
                      }}
                      key={item.id}
                      value={item.value}
                      control={<Radio size='small' />}
                      label={item.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          </>
        ))}
        <Button
          key='reset-button'
          id='filter-reset-button'
          onClick={handleResetFilters}
          fullWidth
          size='small'
        >
          CLEAR
        </Button>
        ,
      </Menu>
    </>
  )
}

export default EventFilter
